import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/api/client';
import { Button } from './PageHeader';
import { Modal } from './Modal';
import { InputField } from './FormFields';

interface AuthResponse {
  success: boolean;
  data: {
    token: string;
    user: {
      id: string;
      email: string;
      name: string;
    };
  };
}

export function AuthButton() {
  const { user, isAuthenticated, login, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDevKeyModal, setShowDevKeyModal] = useState(false);
  const [devKey, setDevKey] = useState('');
  const [error, setError] = useState('');

  const handleDemoLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await api.post<AuthResponse>('/auth/demo');
      await login(response.data.token);
    } catch {
      // Demo might be disabled, try showing dev key modal
      setShowDevKeyModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDevKeyLogin = async () => {
    if (!devKey.trim()) {
      setError('Dev key is required');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      // Make request with X-Dev-Key header
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:7001'}/v1/auth/dev-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Dev-Key': devKey.trim(),
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Authentication failed');
      }

      await login(data.data.token);
      setShowDevKeyModal(false);
      setDevKey('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuthenticated && user) {
    return (
      <div className="flex items-center gap-3">
        <div className="text-sm">
          <span className="text-gray-500">Logged in as</span>{' '}
          <span className="font-medium text-gray-900">{user.email}</span>
        </div>
        <Button variant="secondary" size="sm" onClick={logout}>
          Logout
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button onClick={handleDemoLogin} isLoading={isLoading}>
        Login
      </Button>

      <Modal
        isOpen={showDevKeyModal}
        onClose={() => {
          setShowDevKeyModal(false);
          setError('');
        }}
        title="Enter Dev Key"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Demo login is disabled. Enter your dev API key to authenticate.
          </p>
          <InputField
            label="Dev API Key"
            type="password"
            value={devKey}
            onChange={(e) => setDevKey(e.target.value)}
            placeholder="Enter X-Dev-Key"
            error={error}
          />
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowDevKeyModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleDevKeyLogin} isLoading={isLoading}>
              Login
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
